// Name prompt (§9.1, §14.2): 2–16 characters, pre-filled with a random bean name, a dice button
// that re-rolls (with a tumble), and LET'S BONK. Shown on first launch and before a `/r/` link;
// opened with `{ edit: true }` from the Bean Card or Settings it renames you and goes back.
import { NAME_MAX_LENGTH, cleanName } from '@shared/names';
import { suggestBeanName } from '../../profile/Profile';
import type { LiveScreen } from '../contracts';
import type { UiDeps } from '../deps';
import { beanIcon, identityHex } from '../icons';
import { el, replayClass, setHidden, setText } from '../ui';
import { button, closeButton, setLabel } from '../widgets';

export interface NameProps { edit?: boolean }

export function create(deps: UiDeps): LiveScreen {
  const input = el('input', {
    class: 'text name-input',
    attrs: { type: 'text', maxlength: NAME_MAX_LENGTH, autocomplete: 'off', spellcheck: 'false', autocapitalize: 'words', enterkeyhint: 'go', 'aria-label': 'Bean name', 'data-autofocus': true },
  });
  const error = el('div', { class: 'error-text', attrs: { 'aria-live': 'polite' } });
  const mascot = el('div', { class: 'name-mascot' });
  const dice = button({ icon: 'dice', title: 'Random name', variant: 'cream', cls: 'dice', onClick: () => roll() });
  const lede = el('p', { class: 'name-lede' });
  const ribbon = el('div', { class: 'ribbon' });
  const go = button({ label: "LET'S BONK!", variant: 'pink', size: 'big', type: 'submit', cls: 'wide' });
  const close = closeButton(() => deps.nav.back());
  let edit = false;

  function roll(): void {
    input.value = suggestBeanName();
    error.textContent = '';
    replayClass(dice, 'rolling');
    replayClass(mascot, 'boing');
  }

  const panel = el('form', { class: 'panel hero with-ribbon name-form springy', attrs: { novalidate: true }, on: {
    submit: (e) => {
      e.preventDefault();
      const name = cleanName(input.value);
      if (!name) {
        error.textContent = '2–16 characters, and keep it friendly!';
        replayClass(panel, 'shake');
        deps.services.playUi('error');
        return;
      }
      if (edit) {
        deps.services.profiles.setName(name);
        deps.nav.back();
      } else {
        deps.actions.setName(name);
      }
    },
  } },
    ribbon, close, mascot, lede,
    el('div', { class: 'row name-row' }, input, dice),
    error,
    go);
  const root = el('div', { class: 'screen modal name-screen' }, el('div', { class: 'sunburst' }), panel);
  return {
    el: root,
    // (START) on a gamepad means "done" on a text entry, as on console keyboards.
    onNav(action) {
      if (action !== 'pause') return false;
      panel.requestSubmit();
      return true;
    },
    onShow(props) {
      edit = (props as NameProps | undefined)?.edit === true;
      root.toggleAttribute('data-noback', !edit);
      setHidden(close, !edit);
      setText(ribbon, edit ? 'CHANGE YOUR NAME' : "WHO'S THIS BEAN?");
      setText(lede, edit ? 'Everyone in your rooms will see the new name.' : 'Pick a name for your bean. Everyone in the room will see it!');
      setLabel(go, edit ? 'SAVE NAME' : "LET'S BONK!");
      const p = deps.profile();
      input.value = p.name || suggestBeanName();
      error.textContent = '';
      const pref = p.cosmetics.identityPref < 8 ? p.cosmetics.identityPref : 0;
      const portrait = deps.services.portrait(p.cosmetics, pref, 192);
      mascot.replaceChildren(portrait ?? beanIcon(identityHex(pref, false), 'happy', 'ico mascot-svg'));
      setTimeout(() => input.select(), 0);
    },
  };
}
